import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Collapse, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorageIcon from '@mui/icons-material/Storage';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { resources } from '../../core/services';
import { IconTile } from '../../shared/components/IconTile';
import { Pill } from '../../shared/components/Pill';
import { useLocalStorageState } from '../../shared/hooks/useLocalStorageState';
import { useDocumentGeneration } from '../../shared/hooks/useDocumentGeneration';
import { useAssistantRoles } from '../../ai-assistant/hooks/useAssistantRoles';
import type { DocumentTypeSection } from '../../core/types/data-models';
import { AiAssistantsSection, type AiAssistantsLayout } from './validation-flow/AiAssistantsSection';
import { FlowProgressSection } from './validation-flow/FlowProgressSection';
import { NextStepsSection } from './validation-flow/NextStepsSection';
import { OpenTasksSection, type OpenTask } from './validation-flow/OpenTasksSection';
import { OverviewCard } from './validation-flow/OverviewCard';
import {
  buildBlockedByCategory,
  buildNextSteps,
  buildRemainingByCategory,
  type BlockedNextDoc,
} from './validation-flow/documentTypeCatalog';
import { FLOW_CATEGORY_LABELS, FLOW_CATEGORY_TYPES, type FlowStage } from './validation-flow/flowStages';

/**
 * /projects/:id/validation-flow — project health overview: stat cards, the
 * per-category flow progress, Next Steps & Open Tasks, and the AI Assistants
 * roster. Loads the project's documents and their review/comment/entity/
 * visualization counts here and hands each block its slice.
 */
export function ValidationFlow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = Number(id);

  const { roles: assistantRoles } = useAssistantRoles();
  const generation = useDocumentGeneration(projectId);
  const openDocument = (docId: number) => navigate(`/documents/${docId}`);

  // Expanded by default — collapsed only once the user has explicitly done so before.
  const [nextStepsRowOpen, setNextStepsRowOpen] = useLocalStorageState<boolean>(
    'projectDetail.nextStepsOpen',
    true,
  );
  const [assistantsLayout, setAssistantsLayout] = useState<AiAssistantsLayout>('row');

  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => resources.projects.get(projectId),
    enabled: Number.isFinite(projectId),
  });

  const docsQuery = useQuery({
    queryKey: ['documents', { projectId }],
    queryFn: () => resources.documents.list({ pageSize: 100, filter: { projectId }, sort: '-updatedAt' }),
    enabled: Number.isFinite(projectId),
  });

  const documentTypesQuery = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });

  const categoriesQuery = useQuery({
    queryKey: ['documentCategories'],
    queryFn: () => resources.documentCategories.list({ pageSize: 100, sort: 'order' }),
  });

  const inReviewSectionsQuery = useQuery({
    queryKey: ['document-sections-in-review', projectId],
    queryFn: () =>
      resources.documentSections.list({
        filter: { projectId: [projectId], status: ['IN_REVIEW'] },
        pageSize: 2000,
      }),
    enabled: Number.isFinite(projectId),
  });

  const documentCommentsQuery = useQuery({
    queryKey: ['document-comments', projectId],
    queryFn: () => resources.documentComments.list({ filter: { projectId: [projectId] }, pageSize: 2000 }),
    enabled: Number.isFinite(projectId),
  });

  // Every entity in the project, regardless of tab configuration — feeds the
  // Knowledge Entities stat card, so it's fetched unconditionally.
  const allProjectEntitiesQuery = useQuery({
    queryKey: ['project-entities-all', projectId],
    queryFn: () => resources.entities.list({ filter: { projectId: [projectId] }, pageSize: 2000 }),
    enabled: Number.isFinite(projectId),
  });

  const visualizationsQuery = useQuery({
    queryKey: ['project-document-visualizations', projectId],
    queryFn: () =>
      resources.documentVisualizations.list({ filter: { projectId: [projectId] }, sort: '-createdAt', pageSize: 200 }),
    enabled: Number.isFinite(projectId),
  });

  // Only needed once the row layout is shown.
  const documentPresentationsQuery = useQuery({
    queryKey: ['project-document-presentations', projectId],
    queryFn: () => resources.documentPresentations.list({ filter: { projectId: [projectId] }, pageSize: 500 }),
    enabled: Number.isFinite(projectId) && assistantsLayout === 'row',
  });

  const documentTypeSectionsQuery = useQuery({
    queryKey: ['documentTypeSections'],
    queryFn: () => resources.documentTypeSections.list({ pageSize: 2000, sort: 'defaultOrder' }),
    enabled: assistantsLayout === 'row',
  });

  const allDocumentEntitiesQuery = useQuery({
    queryKey: ['project-document-entities-all', projectId],
    queryFn: () => resources.documentSectionEntities.list({ filter: { projectId: [projectId] }, pageSize: 2000 }),
    enabled: Number.isFinite(projectId),
  });

  // Overview card: how many entity types are configured as "managed project
  // knowledge" (browsable in the console's Managed Knowledge sidebar block).
  const managedEntityTypesApi = resources.projectManagedEntityTypesFor(projectId);
  const managedEntityTypesQuery = useQuery({
    queryKey: ['project-managed-entity-types', projectId],
    queryFn: () => managedEntityTypesApi.list({ pageSize: 200 }),
    enabled: Number.isFinite(projectId),
  });

  const isLoading =
    docsQuery.isPending ||
    documentTypesQuery.isPending ||
    categoriesQuery.isPending ||
    inReviewSectionsQuery.isPending ||
    documentCommentsQuery.isPending ||
    allDocumentEntitiesQuery.isPending;

  const docTypeMap = new Map((documentTypesQuery.data?.data ?? []).map((dt) => [dt.type, dt]));
  const docById = new Map((docsQuery.data?.data ?? []).map((d) => [d.id, d]));
  const visibleDocs = (docsQuery.data?.data ?? []).filter((d) => d.status !== 'IN_GENERATION');

  const inReviewCountByDoc = new Map<number, number>();
  for (const s of inReviewSectionsQuery.data?.data ?? []) {
    inReviewCountByDoc.set(s.documentId, (inReviewCountByDoc.get(s.documentId) ?? 0) + 1);
  }

  const unresolvedCommentCountByDoc = new Map<number, number>();
  for (const c of documentCommentsQuery.data?.data ?? []) {
    if (c.resolvedAt) continue;
    unresolvedCommentCountByDoc.set(c.documentId, (unresolvedCommentCountByDoc.get(c.documentId) ?? 0) + 1);
  }

  const visualizationCountByDoc = new Map<number, number>();
  for (const v of visualizationsQuery.data?.data ?? []) {
    visualizationCountByDoc.set(v.documentId, (visualizationCountByDoc.get(v.documentId) ?? 0) + 1);
  }

  const presentationCountByDoc = new Map<number, number>();
  for (const p of documentPresentationsQuery.data?.data ?? []) {
    presentationCountByDoc.set(p.documentId, (presentationCountByDoc.get(p.documentId) ?? 0) + 1);
  }

  // Distinct entities per document — one entity can be mentioned in more than
  // one section of the same document, so the link rows collapse by entityId.
  const entityCountByDoc = new Map<number, number>();
  {
    const entityIdsByDoc = new Map<number, Set<number>>();
    for (const e of allDocumentEntitiesQuery.data?.data ?? []) {
      let entityIds = entityIdsByDoc.get(e.documentId);
      if (!entityIds) {
        entityIds = new Set<number>();
        entityIdsByDoc.set(e.documentId, entityIds);
      }
      entityIds.add(e.entityId);
    }
    for (const [docId, entityIds] of entityIdsByDoc) {
      entityCountByDoc.set(docId, entityIds.size);
    }
  }

  // Project-wide entity count for the overview cards — one row per entity, so
  // this is the list length.
  const entityCount = allProjectEntitiesQuery.data?.data.length ?? 0;
  const managedEntityTypesCount = managedEntityTypesQuery.data?.data.length ?? 0;

  const openTasks: OpenTask[] = [];
  for (const [docId, count] of inReviewCountByDoc) {
    const doc = docById.get(docId);
    if (doc) openTasks.push({ kind: 'review', doc, count });
  }
  for (const [docId, count] of unresolvedCommentCountByDoc) {
    const doc = docById.get(docId);
    if (doc) openTasks.push({ kind: 'comments', doc, count });
  }
  openTasks.sort((a, b) => b.count - a.count);

  const existingDocTypesSet = new Set((docsQuery.data?.data ?? []).map((d) => d.documentType));
  const recommendedItems =
    !documentTypesQuery.isPending && !categoriesQuery.isPending
      ? buildNextSteps(documentTypesQuery.data?.data ?? [], categoriesQuery.data?.data ?? [], existingDocTypesSet)
      : [];
  const nextStepsCount = recommendedItems.length;

  const blockedByCategory = documentTypesQuery.isPending
    ? new Map<string, BlockedNextDoc>()
    : buildBlockedByCategory(documentTypesQuery.data?.data ?? [], existingDocTypesSet);
  const remainingByCategory = documentTypesQuery.isPending
    ? new Map<string, number>()
    : buildRemainingByCategory(documentTypesQuery.data?.data ?? [], existingDocTypesSet);

  const rootSectionsByDocType = new Map<string, DocumentTypeSection[]>();
  for (const s of documentTypeSectionsQuery.data?.data ?? []) {
    if (s.parentDocumentTypeSectionType !== null) continue;
    const list = rootSectionsByDocType.get(s.documentType) ?? [];
    if (list.length < 5) list.push(s);
    rootSectionsByDocType.set(s.documentType, list);
  }

  // Overview card: "Validation progress" — active, top-level document types in
  // the Discovery/Market and Business Case/Strategy categories that already
  // have a generated document, out of every active document type in those
  // categories.
  const validationCategoryTypes = new Set(['DISCOVERY_MARKET_INTELLIGENCE', 'BUSINESS_CASE_STRATEGY']);
  const validationDocTypes = (documentTypesQuery.data?.data ?? []).filter(
    (dt) => dt.status === 'Active' && dt.parentDocumentType === null && validationCategoryTypes.has(dt.categoryType),
  );
  const validationDoneCount = validationDocTypes.filter((dt) => existingDocTypesSet.has(dt.type)).length;
  const validationTotalCount = validationDocTypes.length;
  const validationProgressPercent =
    validationTotalCount > 0 ? Math.round((validationDoneCount / validationTotalCount) * 100) : 0;

  // "Flow progress" row: active, top-level document types per flow category
  // (Discovery & Market through Product Definition) — same "done / total,
  // active only" logic as the Validation Progress card above, broken out by
  // category and covering all 4 stages instead of summing just the first two.
  const flowStages: FlowStage[] = FLOW_CATEGORY_TYPES.map((categoryType) => {
    const docTypesInCategory = (documentTypesQuery.data?.data ?? []).filter(
      (dt) => dt.status === 'Active' && dt.parentDocumentType === null && dt.categoryType === categoryType,
    );
    return {
      categoryType,
      name: FLOW_CATEGORY_LABELS[categoryType],
      done: docTypesInCategory.filter((dt) => existingDocTypesSet.has(dt.type)).length,
      total: docTypesInCategory.length,
    };
  });
  const flowDoneTotal = flowStages.reduce((sum, s) => sum + s.done, 0);
  const flowCountTotal = flowStages.reduce((sum, s) => sum + s.total, 0);
  const flowProgressPercent = flowCountTotal > 0 ? Math.round((flowDoneTotal / flowCountTotal) * 100) : 0;

  const project = projectQuery.data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5, pb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton size="small" onClick={() => navigate(`/projects/${projectId}`)}>
          <KeyboardBackspaceIcon fontSize="small" />
        </IconButton>
        <IconTile icon={<TimelineIcon fontSize="inherit" />} color="primary" size="md" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-sans)',
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: 'var(--color-ink)',
            }}
          >
            Validation flow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {project ? project.name : 'Validation progress, flow stages and open work'}
          </Typography>
        </Box>
      </Stack>

      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={160} />
          ))}
        </Stack>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <OverviewCard
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              label="Validation Progress"
              value={`${validationDoneCount} / ${validationTotalCount}`}
              progressPercent={validationProgressPercent}
              description="Discovery & Market and Business Case & Strategy artifacts"
              color="primary"
            />
            <OverviewCard
              icon={<AccountTreeIcon fontSize="inherit" />}
              label="Knowledge Entities"
              value={entityCount}
              description="Distinct entities extracted across this project"
              color="cyan"
            />
            <OverviewCard
              icon={<StorageIcon fontSize="inherit" />}
              label="Managed Entity Types"
              value={managedEntityTypesCount}
              description="Entity types browsable in Managed Knowledge"
              color="secondary"
            />
            <OverviewCard
              icon={<LightbulbIcon fontSize="inherit" />}
              label="Active Recommendations"
              value={nextStepsCount}
              description="Documents ready to generate next"
              color="warning"
            />
          </Box>

          <FlowProgressSection stages={flowStages} percent={flowProgressPercent} roles={assistantRoles} />

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={() => setNextStepsRowOpen((o) => !o)}
              sx={{ cursor: 'pointer', userSelect: 'none', mb: nextStepsRowOpen ? 1.5 : 0 }}
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                {nextStepsRowOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
              <Typography variant="subtitle2" fontWeight={700}>
                Next Steps & Open Tasks
              </Typography>
              {nextStepsCount > 0 && <Pill color="primary">{nextStepsCount} Pending</Pill>}
              {openTasks.length > 0 && <Pill color="warning">{openTasks.length} Open</Pill>}
            </Stack>
            <Collapse in={nextStepsRowOpen} unmountOnExit>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <NextStepsSection
                  docTypes={documentTypesQuery.data?.data ?? []}
                  categories={categoriesQuery.data?.data ?? []}
                  existingDocumentTypes={existingDocTypesSet}
                  roles={assistantRoles}
                  onGenerate={generation.requestGenerate}
                />
                <OpenTasksSection
                  tasks={openTasks}
                  docTypeMap={docTypeMap}
                  roles={assistantRoles}
                  onOpenDocument={openDocument}
                />
              </Stack>
            </Collapse>
          </Box>

          <AiAssistantsSection
            docs={visibleDocs}
            docTypeMap={docTypeMap}
            inReviewCountByDoc={inReviewCountByDoc}
            unresolvedCommentCountByDoc={unresolvedCommentCountByDoc}
            entityCountByDoc={entityCountByDoc}
            visualizationCountByDoc={visualizationCountByDoc}
            presentationCountByDoc={presentationCountByDoc}
            recommendedItems={recommendedItems}
            blockedByCategory={blockedByCategory}
            remainingByCategory={remainingByCategory}
            rootSectionsByDocType={rootSectionsByDocType}
            layout={assistantsLayout}
            onLayoutChange={setAssistantsLayout}
            onOpenDocument={openDocument}
            onGenerateRecommended={generation.requestGenerate}
          />
        </>
      )}

      {generation.dialog}
    </Box>
  );
}
